import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, Check, Loader2, BellOff, ShieldCheck, Sparkles, CalendarDays, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { PublicLayout } from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";
import { api, errorMessage } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const PERKS = [
  { icon: Sparkles, title: "Nye funksjoner først", text: "Vi sender en kort melding når Right in får noe nytt som gjør arrangørlivet enklere." },
  { icon: Lightbulb, title: "Tips som fungerer", text: "Konkrete råd om billettsalg, plakater, innsjekk og hvordan du får folk til å komme." },
  { icon: CalendarDays, title: "Sjelden – og relevant", text: "Maks noen få e-poster i året. Ingen spam, og du melder deg av med ett klikk." },
];

export default function NewsletterPage() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const e = params.get("e") || "";
  const sig = params.get("sig") || "";
  const org = params.get("org") || "";
  const prospectId = params.get("p") || "";
  const unsubscribeMode = params.get("avmeld") === "1";

  const [checking, setChecking] = useState(!!(e && sig));
  const [linkValid, setLinkValid] = useState(false);
  const [existingStatus, setExistingStatus] = useState("");
  const [consentText, setConsentText] = useState("Ja, jeg vil motta nyhetsbrev fra Right in på e-post. Jeg kan melde meg av når som helst.");
  const [email, setEmail] = useState(e);
  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState(org);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // "confirmed" | "pending" | "unsubscribed"

  useEffect(() => {
    if (!(e && sig)) return;
    api.get("/newsletter/status", { params: { e, sig } })
      .then((res) => {
        setLinkValid(res.data.valid_sig);
        setExistingStatus(res.data.status || "");
        if (res.data.consent_text) setConsentText(res.data.consent_text);
        if (res.data.valid_sig) {
          setEmail(res.data.email);
          if (res.data.name) setName(res.data.name);
          if (res.data.org_name && !org) setOrgName(res.data.org_name);
        }
      })
      .catch(() => setLinkValid(false))
      .finally(() => setChecking(false));
  }, [e, sig]); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = async (ev) => {
    ev.preventDefault();
    if (!consent) {
      toast.error(t("Kryss av for at du vil motta nyhetsbrevet"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/newsletter/subscribe", {
        email, name, org_name: orgName, consent, consent_text: t(consentText), sig: linkValid ? sig : "", prospect_org_id: linkValid ? prospectId : "",
      });
      setResult(res.data.status);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const unsubscribe = async () => {
    setSubmitting(true);
    try {
      await api.post("/newsletter/unsubscribe", { email, sig });
      setResult("unsubscribed");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <section className="hero-decor border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-left">
          <Badge variant="secondary" className="text-xs font-medium mb-5" data-testid="newsletter-page-badge">
            <Mail className="w-3.5 h-3.5 mr-1.5" /> {t("Nyhetsbrev")}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="newsletter-page-title">
            {unsubscribeMode ? t("Meld deg av nyhetsbrevet") : t("Nyheter og tips fra Right in")}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl" data-testid="newsletter-page-intro">
            {unsubscribeMode
              ? t("Leit at du vil gå. Ett klikk under, så sender vi ikke flere nyhetsbrev til denne adressen.")
              : t("Et kort nyhetsbrev for lag, foreninger og kulturaktører – om nye funksjoner i Right in, tips til bedre billettsalg og det som skjer i Sunnhordland. Du velger selv, og du kan melde deg av når som helst.")}
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <Card className="border-border" data-testid="newsletter-card">
            <CardContent className="p-6 sm:p-8">
              {checking ? (
                <div className="space-y-3" data-testid="newsletter-loading"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /><Skeleton className="h-12 w-40" /></div>
              ) : result === "confirmed" ? (
                <div className="space-y-3" data-testid="newsletter-confirmed">
                  <div className="w-12 h-12 rounded-full bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] flex items-center justify-center"><Check className="w-6 h-6" /></div>
                  <h2 className="text-2xl font-heading font-semibold">{t("Takk – du er påmeldt!")}</h2>
                  <p className="text-muted-foreground">{t("Nyhetsbrevet sendes til")} <strong className="text-foreground break-all">{email}</strong>. {t("Hver e-post har en avmeldingslenke, så du bestemmer selv hvor lenge du vil være med.")}</p>
                  <Button asChild variant="secondary" data-testid="newsletter-confirmed-cta"><Link to="/for-arrangorer">{t("Les mer om Right in")}</Link></Button>
                </div>
              ) : result === "pending" ? (
                <div className="space-y-3" data-testid="newsletter-pending">
                  <div className="w-12 h-12 rounded-full bg-brand-soft text-brand flex items-center justify-center"><Mail className="w-6 h-6" /></div>
                  <h2 className="text-2xl font-heading font-semibold">{t("Sjekk e-posten din")}</h2>
                  <p className="text-muted-foreground">{t("Vi har sendt en bekreftelse til")} <strong className="text-foreground break-all">{email}</strong>. {t("Klikk på «Bekreft abonnementet» i e-posten for å fullføre. Finner du den ikke? Se i søppelpost-mappen.")}</p>
                </div>
              ) : result === "unsubscribed" ? (
                <div className="space-y-3" data-testid="newsletter-unsubscribed">
                  <div className="w-12 h-12 rounded-full bg-secondary text-muted-foreground flex items-center justify-center"><BellOff className="w-6 h-6" /></div>
                  <h2 className="text-2xl font-heading font-semibold">{t("Du er meldt av")}</h2>
                  <p className="text-muted-foreground">{t("Vi sender ikke flere nyhetsbrev til")} <strong className="text-foreground break-all">{email}</strong>. {t("Ombestemmer du deg, er du velkommen tilbake når som helst.")}</p>
                  <Button variant="secondary" onClick={() => { setResult(null); setConsent(false); }} data-testid="newsletter-resubscribe">{t("Abonner igjen")}</Button>
                </div>
              ) : unsubscribeMode ? (
                linkValid ? (
                  <div className="space-y-4" data-testid="newsletter-unsubscribe-form">
                    <p className="text-sm text-muted-foreground">{t("Adresse:")} <strong className="text-foreground break-all" data-testid="newsletter-unsubscribe-email">{email}</strong></p>
                    {existingStatus === "unsubscribed" && <p className="text-sm text-muted-foreground" data-testid="newsletter-already-unsubscribed">{t("Denne adressen er allerede meldt av.")}</p>}
                    <Button onClick={unsubscribe} disabled={submitting} variant="destructive" className="h-11" data-testid="newsletter-unsubscribe-button">
                      {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BellOff className="w-4 h-4 mr-2" />} {t("Meld meg av nyhetsbrevet")}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground" data-testid="newsletter-link-invalid">{t("Avmeldingslenken er ugyldig eller utløpt. Bruk lenken nederst i nyhetsbrevet, eller svar på e-posten så hjelper vi deg.")}</p>
                )
              ) : (
                <form onSubmit={subscribe} className="space-y-5" data-testid="newsletter-form">
                  {linkValid && (
                    <div className="rounded-xl bg-brand-soft text-brand text-sm px-4 py-3 flex items-start gap-2" data-testid="newsletter-link-notice">
                      <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{t("Du kom hit fra en e-post vi sendte til")} <strong className="break-all">{email}</strong>{orgName ? <> {t("på vegne av")} {orgName}</> : null}. {t("Ett klikk under, så er du påmeldt – ingen ekstra bekreftelse nødvendig.")}</span>
                    </div>
                  )}
                  {existingStatus === "confirmed" && !result && (
                    <p className="text-sm text-muted-foreground" data-testid="newsletter-already-confirmed">{t("Denne adressen abonnerer allerede. Du kan oppdatere navn og organisasjon under.")}</p>
                  )}
                  <div className="space-y-1.5">
                    <Label htmlFor="nl-email">{t("E-post")}</Label>
                    <Input id="nl-email" type="email" required value={email} onChange={(ev) => setEmail(ev.target.value)} readOnly={linkValid} className={linkValid ? "bg-secondary/60" : ""} placeholder="deg@organisasjonen.no" data-testid="newsletter-email-input" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="nl-name">{t("Navn")} <span className="text-muted-foreground font-normal">{t("(valgfritt)")}</span></Label>
                      <Input id="nl-name" value={name} onChange={(ev) => setName(ev.target.value)} placeholder={t("Fornavn Etternavn")} data-testid="newsletter-name-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="nl-org">{t("Organisasjon")} <span className="text-muted-foreground font-normal">{t("(valgfritt)")}</span></Label>
                      <Input id="nl-org" value={orgName} onChange={(ev) => setOrgName(ev.target.value)} placeholder={t("Laget, foreningen eller kulturhuset")} data-testid="newsletter-org-input" />
                    </div>
                  </div>
                  <label className="flex items-start gap-3 rounded-xl border border-border p-4 cursor-pointer hover:bg-secondary/40 transition-colors duration-150">
                    <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="newsletter-consent-checkbox" />
                    <span className="text-sm" data-testid="newsletter-consent-text">{t(consentText)}</span>
                  </label>
                  <Button type="submit" size="lg" className="h-12 px-6" disabled={submitting || !consent || !email} data-testid="newsletter-subscribe-button">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                    {t("Abonner på nyhetsbrevet")}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    {t("Vi lagrer e-postadressen, eventuelt navn og organisasjon, og tidspunktet for samtykket. Opplysningene brukes bare til nyhetsbrevet og deles ikke med andre.")}{" "}
                    {t("Les mer i")} <Link to="/personvern" className="underline underline-offset-4" data-testid="newsletter-privacy-link">{t("personvernerklæringen")}</Link>.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
        <div className="lg:col-span-2 space-y-4">
          {PERKS.map((p) => (
            <div key={p.title} className="flex items-start gap-3" data-testid="newsletter-perk">
              <span className="w-10 h-10 rounded-xl bg-brand-soft text-brand flex items-center justify-center shrink-0"><p.icon className="w-5 h-5" /></span>
              <div>
                <p className="font-heading font-semibold">{t(p.title)}</p>
                <p className="text-sm text-muted-foreground">{t(p.text)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </PublicLayout>
  );
}
